import { useState, type FormEvent } from 'react'
import { Send } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ChatResponse {
  reply: string
  conversationId: string
}

export function AssistantPage() {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!input.trim()) return
    const userMsg: ChatMessage = { role: 'user', content: input }
    setMessages((m) => [...m, userMsg])
    setInput('')
    setSubmitting(true)
    setError(null)
    try {
      const data = await artistFetch<ChatResponse>('/assistant/chat', {
        method: 'POST',
        body: { message: userMsg.content, conversationId },
      })
      setConversationId(data.conversationId)
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }])
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Assistant"
        subtitle="Ask about tour travel, flights, hotels, and show logistics. Text chat only — type your question. General AI use is blocked; usage is billed to the artist."
      />
      <div className="flex-1 space-y-4 overflow-y-auto rounded-lg border border-ovation-ink/10 bg-white p-6">
        {messages.length === 0 && (
          <div className="text-sm text-ovation-muted">
            Try: <em>How many confirmed shows do we have in Q3?</em> or{' '}
            <em>Which broker is fastest in Europe?</em>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] whitespace-pre-wrap rounded-lg px-4 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-ovation-ink text-ovation-paper'
                  : 'border border-ovation-ink/10 bg-ovation-paper/40'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {submitting && <div className="text-sm text-ovation-muted">Thinking…</div>}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
      <form onSubmit={onSubmit} className="mt-4 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Ovation…"
          className="flex-1 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm focus:border-ovation-ink focus:outline-none"
        />
        <button
          type="submit"
          disabled={submitting || !input.trim()}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          Send
        </button>
      </form>
    </div>
  )
}
